import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { apiGateway } from "../../utils/config";
import { handleSetAccessToken } from "../../utils/constants";
import { firebase } from "../../firebase/firebase.utils";
import { axiosPublic } from "../../api/axiosCustom";
import { toast } from "react-toastify";
import { FaCheckCircle } from "react-icons/fa";
import { FiArrowLeft } from "react-icons/fi";
import {
  handleGetFirebaseUid,
  handleGetUserSuccess,
  handleGetUserIsLoading,
} from "../../redux/Auth/action";
import {
  autoReadSMS,
  generateEncryptedString,
  handleGenerateRandomString,
  isMobileDevice,
} from "../../utils/utils";
import {
  aes_secret_key,
  environment,
  truecaller_app_key,
} from "../../utils/constants/env.constants";
import {
  handleAutoReadOtpEvent,
  handleLoginSuccessEvent,
  handleWebTrueCallerLoginEvent,
} from "../../utils/helperEventFunctions";
import Cookies from "js-cookie";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import ButtonLoader from "../Icons/ButtonLoader";
import useStore from "../../store/UseStore";
import OverlayLoader from "../common/OverlayLoader";

const LoginComponent = () => {
  const [otp, setOtp] = useState("");
  const [phone, setPhone] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [verifyTrueCallerLoading, setVerifyTrueCallerLoading] = useState(false);
  const { setOpenPaymentModal } = useStore();
  const otpInputRef = useRef(null);
  const router = useRouter();
  const { fromPath, seriesSlug } = router.query;
  const dispatch = useDispatch();
  const axiosPrivate = useAxiosPrivate();
  const firebase_uid = useSelector((state) => state.currentUser?.firebaseUID);

  // Validates users and stores in redux
  const handleValidateUserAndStoreDetails = async () => {
    try {
      const url = `${apiGateway}/api/v1/users/me/`;
      const response = await axiosPrivate.get(url);
      const { data } = response;
      if (data) {
        const isPremium = response?.data?.user?.is_premium;
        setIsLoading(false);
        dispatch(handleGetUserIsLoading(false));
        dispatch(handleGetUserSuccess(data?.user));
        setVerifyTrueCallerLoading(false);
        if (isPremium) {
          if (seriesSlug) {
            router.replace(`/video/${seriesSlug}/`);
          } else {
            router.replace("/app/");
          }
        } else {
          if (fromPath && fromPath === "premium") {
            setOpenPaymentModal(true);
            router.replace(
              `/premium${seriesSlug ? `?seriesSlug=${seriesSlug}` : ""}`
            );
          } else {
            router.replace("/premium/");
          }
        }
        toast.success(`Hey ${data?.user?.name}, good to see you again!`, {
          icon: "👋",
          duration: 5000,
        });
      }
    } catch (error) {
      setIsLoading(false);
      setVerifyTrueCallerLoading(false);
      toast.error(error?.response.data?.error_message || error?.message);
      dispatch(handleGetUserIsLoading(false));
    }
  };

  // API Call to sent OPT
  const handleGetOTP = async () => {
    if (phone.length === 10) {
      try {
        setIsLoading(true);
        const apiVersion = environment === "staging" ? "v1" : "v1.1";
        const url = `${apiGateway}/api/${apiVersion}/auth/send-otp/`;
        const payload = {
          phone_number: phone,
          country_code: "+91",
          request_source: "web",
          request_id: generateEncryptedString({
            secretKey: aes_secret_key,
          }),
        };
        const response = await axiosPublic.post(url, payload);
        const { data } = response;
        if (data) {
          setOtpSent(true);
          setIsLoading(false);
          setVerificationId(data?.verification_id);
          toast.success(`OTP send to +91 xxxxxxxx${phone?.substr(8, 10)}.`, {
            icon: <FaCheckCircle />,
          });
        }
      } catch (error) {
        setOtpSent(false);
        setIsLoading(false);
        setVerificationId("");
        toast.error(error?.response?.data?.error_message || error?.message);
      }
    }
  };

  // setting the cookies
  const handleSetCookies = ({ data }) => {
    if (process.env.NODE_ENV === "development") {
      Cookies.set("access_token", data?.access_token, {
        expires: 7,
      });
      Cookies.set("refresh_token", data?.refresh_token, {
        expires: 7,
      });
    }
    Cookies.set("refresh_token_expiry_seekho", data?.refresh_token_expiry, {
      expires: 7,
    });
    Cookies.set("access_token_expiry_seekho", data?.access_token_expiry, {
      expires: 7,
    });
    Cookies.set(
      "renew_access_token_before_seekho",
      data?.renew_access_token_before,
      { expires: 7 }
    );
    Cookies.set(
      "renew_refresh_token_before_seekho",
      data?.renew_refresh_token_before,
      { expires: 7 }
    );
  };

  // function make user login on firebase and sets required details in localStorage
  const handleCommonVerifier = async ({ response }) => {
    handleSetCookies({ data: response?.data }); // setting the cookies
    // just a confirmation, to find out user has loggedIn. access_token will be set by server &
    // hence it is not available in prod, so setting firebase_token as a sign, user has loggedIn
    handleSetAccessToken({
      token: response?.data?.access_token || response?.data?.firebase_token,
    });
    try {
      // Making the user sign in on Firebase
      await firebase
        .auth()
        .signInWithCustomToken(response?.data?.firebase_token);
      // User is signed in, fetch the user details from Firebase
      const currentUser = firebase.auth().currentUser;
      if (currentUser) {
        handleLoginSuccessEvent({ firebase_uid });
        currentUser?.getIdToken().then((data) => {
          if (data) {
            handleValidateUserAndStoreDetails(data);
            dispatch(handleGetFirebaseUid(currentUser?.uid));
          }
        });
      }
    } catch (error) {
      console.warn("Error fetching user details:", error.message);
    }
  };

  // Verify OTP & LOGIN
  const handleVerifyOtpAndLogin = async () => {
    if (otp.length === 6) {
      try {
        setIsLoading(true);
        const hostEndPoint =
          process.env.NODE_ENV !== "development"
            ? "/api/v1/auth/web/verify-otp/"
            : "/api/v1/auth/verify-otp/";
        const url = `${apiGateway}${hostEndPoint}`;
        const payload = {
          otp,
          phone_number: phone,
          country_code: "+91",
          verification_id: verificationId,
        };
        const response = await axiosPublic.post(url, payload);
        if (response?.data) {
          handleCommonVerifier({ response });
        }
      } catch (error) {
        setIsLoading(false);
        toast.error(error?.response?.data?.error_message || error?.message);
      }
    }
  };

  const handleKeyDownPhone = (e) => {
    if (e.key === "Enter" && phone.length === 10) {
      handleGetOTP();
    }
  };

  //  When click enter after entering OTP
  const handleKeyDownOtp = (e) => {
    if (e.key === "Enter" && otp.length === 6) {
      handleVerifyOtpAndLogin();
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length <= 10) setPhone(value);
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length <= 6) setOtp(value);
  };

  //  AUTO read OTP
  const handleCallBackForOTP = (x) => {
    setOtp(x);
    handleAutoReadOtpEvent({
      source_screen: "login",
      status: "clicked",
    });
  };

  // ! TRUECALLER LOGIN START
  const handleVerifyWithTrueCaller = async ({ reqNonce }) => {
    try {
      setVerifyTrueCallerLoading(true);
      const url = `${apiGateway}/api/v1/auth/website/truecaller-verify/`;
      const request_id = reqNonce;
      const payload = { request_id };
      const response = await axiosPublic.post(url, payload);
      const userReject = response?.data?.truecaller_status;
      const isReject =
        userReject === "user_rejected" || userReject === "use_another_number";
      if (response?.status === 200) {
        if (isReject) {
          setVerifyTrueCallerLoading(false);
          handleWebTrueCallerLoginEvent({
            firebase_uid,
            source_screen: "login",
            status: "Truecaller login rejected",
          });
        }
        handleCommonVerifier({ response });
        handleWebTrueCallerLoginEvent({
          firebase_uid,
          source_screen: "login",
          status: "Truecaller login success",
        });
        return true; // returning true, so that api can be called only once
      }
    } catch (error) {
      console.error(error);
    }
    return false; // returning false, so that api can be called only 3 times
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleCallVerifyWithTrueCaller = async ({ reqNonce }) => {
    const intervals = [1000, 2000, 3000]; // Intervals in milliseconds
    for (let i = 0; i < intervals.length; i++) {
      const success = await handleVerifyWithTrueCaller({ reqNonce });
      if (success) break;
      if (i === intervals.length - 1) {
        // If this is the last attempt and it fails, show alert and set loading to false
        toast.error("Truecaller login failed, Try via mobile number");
        setVerifyTrueCallerLoading(false);
      } else {
        await delay(intervals[i]);
      }
    }
  };

  const handleHelper = ({ reqNonce }) => {
    setTimeout(() => {
      if (document.hasFocus()) {
        handleCallVerifyWithTrueCaller({ reqNonce });
      } else {
        handleHelper({ reqNonce });
      }
    }, 600);
  };

  const handleOpenTrueCallerVerification = () => {
    if (typeof window !== "undefined") {
      const reqNonce = handleGenerateRandomString({ length: 20 }); // generating unique a requestId for every login
      const truecallerVerificationURL = `truecallersdk://truesdk/web_verify?requestNonce=${reqNonce}&partnerKey=${truecaller_app_key}&partnerName=Seekho&lang=en&skipOption=useanothermethod`;
      window.location = truecallerVerificationURL;

      setTimeout(() => {
        if (document.hasFocus()) {
          // Truecaller app not present on the device
          // Redirect the user to your alternate verification page
          // You can handle this redirection logic here
        } else {
          handleHelper({ reqNonce });
          handleWebTrueCallerLoginEvent({
            firebase_uid,
            source_screen: "login",
            status: "Truecaller pop up viewed",
          });
        }
      }, 600);
    }
  };
  // ! TRUECALLER LOGIN END

  useEffect(() => {
    if (otpSent) autoReadSMS(handleCallBackForOTP);
  }, [otpSent]);

  useEffect(() => {
    if (otpSent && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [otpSent]);

  //  TRUECALLER
  useEffect(() => {
    if (isMobileDevice()) {
      handleOpenTrueCallerVerification();
    }
  }, []);

  return (
    <section className="relative flex flex-col items-center justify-center min-h-screen overflow-hidden bg-gradient-to-b from-[#100c1c] via-[#1b0f33] to-[#2a0c46] text-white px-6">
      {/* Enhanced Background Visual Glow */}
      <div className="absolute inset-0">
        {/* Top-left glow */}
        <div className="absolute w-[900px] h-[900px] bg-purple-600/35 blur-[180px] rounded-full top-[-250px] left-[-250px] animate-pulse" />
        {/* Bottom-right glow */}
        <div className="absolute w-[700px] h-[700px] bg-fuchsia-500/25 blur-[160px] rounded-full bottom-[-250px] right-[-250px] animate-pulse" />
        {/* Central soft glow */}
        <div className="absolute w-[500px] h-[500px] bg-purple-400/20 blur-[180px] rounded-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
      </div>

      {verifyTrueCallerLoading && <OverlayLoader className="opacity-[75%]" />}

      {/* Go Back Button */}
      <button
        onClick={() => router.back()}
        className="absolute top-6 left-6 z-20"
      >
        <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-gray-300 hover:text-white font-lexend text-sm transition-all duration-300 backdrop-blur-sm">
          <FiArrowLeft className="text-[18px]" />
          Go Back
        </span>
      </button>

      {/* Logo */}
      <div className="relative z-10 mb-10 mt-10">
        <img
          src="/assets/headerlogo.webp"
          alt="Seekho"
          className="w-[180px] mx-auto hover:scale-105 transition-transform duration-300"
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
      </div>

      {/* Title */}
      <div className="relative z-10 text-center mb-12">
        <h1 className="text-[34px] font-lexend font-semibold mb-3">
          Welcome Back 👋
        </h1>
        <p className="text-gray-300 text-[16px] font-lexend">
          Let’s get you back to learning amazing things at Seekho.
        </p>
      </div>

      {/* Phone & OTP Section */}
      <div className="relative z-10 w-full max-w-sm flex flex-col items-center gap-6">
        {/* Phone Input */}
        {!otpSent && (
          <>
            <input
              type="tel"
              placeholder="Enter your phone number"
              value={phone}
              onChange={handlePhoneChange}
              onKeyDown={handleKeyDownPhone}
              className="w-full px-5 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none font-lexend transition-all"
            />

            <button
              onClick={handleGetOTP}
              disabled={phone.length !== 10 || isLoading}
              className={`w-full py-3 rounded-full text-lg font-lexend transition-all duration-300 ${
                phone.length === 10 && !isLoading
                  ? "bg-gradient-to-r from-purple-600 to-fuchsia-500 hover:scale-[1.03]"
                  : "bg-gray-700 cursor-not-allowed opacity-60"
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <ButtonLoader />
                  <span>Sending...</span>
                </div>
              ) : (
                "Send OTP"
              )}
            </button>
          </>
        )}

        {/* OTP Input */}
        {otpSent && (
          <>
            <input
              type="text"
              placeholder="Enter OTP"
              value={otp}
              ref={otpInputRef}
              onChange={handleOtpChange}
              onKeyDown={handleKeyDownOtp}
              className="w-full px-5 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none font-lexend transition-all"
            />
            <button
              onClick={handleVerifyOtpAndLogin}
              disabled={otp.length !== 6 || isLoading}
              className={`w-full py-3 rounded-full text-lg font-lexend transition-all duration-300 ${
                otp.length === 6 && !isLoading
                  ? "bg-gradient-to-r from-purple-600 to-fuchsia-500 hover:scale-[1.03]"
                  : "bg-gray-700 cursor-not-allowed opacity-60"
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <ButtonLoader />
                  <span>Verifying...</span>
                </div>
              ) : (
                "Login"
              )}
            </button>
          </>
        )}

        {/* Hint */}
        <span className="text-gray-400 text-sm mt-2 font-lexend">
          We’ll never share your number with anyone else*.
        </span>
      </div>

      {/* Footer */}
      <div className="relative z-10 text-center mt-16 text-sm text-gray-400 font-lexend">
        By continuing, you agree to Seekho’s{" "}
        <a
          href="/terms-and-conditions"
          className="text-purple-400 hover:underline"
        >
          Terms
        </a>{" "}
        &{" "}
        <a href="/privacy-policy/" className="text-purple-400 hover:underline">
          Privacy Policy
        </a>
        .
      </div>
    </section>
  );
};

export default LoginComponent;
